import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { AddCircleIcons, SearchIcons } from '../../../../icons/icons';
import { FrameworkPagination, SkillsGrid } from '../components';
import { SORT_OPTIONS } from '../constants';
import { useSkillsManagement } from '../hooks';
import { SkillsManagementProps, SortOption } from '../interfaces';

export const SkillsManagementContainer: React.FC<SkillsManagementProps> = ({
  skills = [],
  subSkillList = [],
}) => {
  const { t } = useTranslation(NS.adminPages);
  const {
    newSkillName,
    setNewSkillName,
    searchQuery,
    setSearchQuery,
    currentPage,
    sortBy,
    setSortBy,
    expandedSkills,
    itemsPerPage,
    sortedAndFilteredSkills,
    totalPages,
    paginatedSkills,
    getSubSkillsForSkill,
    handleAddSkill,
    handleDeleteSkill,
    handleDeleteSubSkill,
    toggleSkillExpansion,
    handlePageChange,
  } = useSkillsManagement({ skills, subSkillList });

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header & Controls Section */}
      <Paper
        variant='outlined'
        sx={{
          borderRadius: '6px',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            component='h2'
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.01em',
            }}
          >
            {t(ADMIN_PAGES_KEYS.assessmentFramework.skillsCount, {
              count: sortedAndFilteredSkills.length,
            })}
          </Typography>

          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
              {t(ADMIN_PAGES_KEYS.assessmentFramework.sortLabel)}
            </Typography>
            <Select
              size='small'
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '6px',
                minWidth: 120,
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {/* Search Box */}
          <Box sx={{ flex: 1, minWidth: 220, maxWidth: { xs: '100%', md: 360 } }}>
            <TextField
              fullWidth
              size='small'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t(ADMIN_PAGES_KEYS.assessmentFramework.searchPlaceholder)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcons style={{ width: 16, height: 16, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>

          {/* New Skill Name Input */}
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <TextField
              fullWidth
              size='small'
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddSkill();
                }
              }}
              placeholder={t(ADMIN_PAGES_KEYS.assessmentFramework.addSkillPlaceholder)}
              sx={{
                bgcolor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>

          {/* Add Button */}
          <Button
            variant='contained'
            onClick={handleAddSkill}
            disabled={!newSkillName.trim()}
            startIcon={<AddCircleIcons style={{ width: 16, height: 16 }} />}
            sx={{
              borderRadius: '6px',
              px: 2.5,
              py: 0.9,
              bgcolor: '#093725',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              flexShrink: 0,
              '&:hover': {
                bgcolor: '#062619',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              },
            }}
          >
            {t(ADMIN_PAGES_KEYS.assessmentFramework.add)}
          </Button>
        </Box>
      </Paper>

      {/* Grid & Pagination Section */}
      <Paper
        variant='outlined'
        sx={{
          borderRadius: '6px',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <SkillsGrid
          skills={paginatedSkills}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          expandedSkills={expandedSkills}
          searchQuery={searchQuery}
          getSubSkillsForSkill={getSubSkillsForSkill}
          onToggleExpand={toggleSkillExpansion}
          onDeleteSkill={handleDeleteSkill}
          onDeleteSubSkill={handleDeleteSubSkill}
        />

        <FrameworkPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={sortedAndFilteredSkills.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </Paper>
    </Box>
  );
};
